// Top structural patterns — presentation-only ordering of the authoritative
// pattern universe (market.patternCards) by existing strength.displayScoreExact.
// No new score, no persisted rank. State fields render the published labels
// verbatim as compact chips; header tooltips explain the semantic dimensions.
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkline } from "./charts";
import InfoTip from "./InfoTip";
import { zoneColor } from "./LeadersChart";

const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");
// Authoritative WP5 displayTitle (Commit-6 hydrated) with canonical patternLabel
// fallback. Never synthesized client-side.
export const patternDisplayTitle = (card) => card?.displayTitle || card?.patternLabel || "—";

// Published state label → chip family. Rendering families only — the published
// label itself is always shown verbatim; no new classification is invented.
const CHIP_FAMILY = {
    STRENGTHENING: "pos", STRONG_STRENGTHENING: "pos", ACCELERATING: "pos", BROADENING: "pos", EXPANDING: "pos",
    STABLE: "neu", STEADY: "neu", PERSISTENT: "neu",
    SOFTENING: "warn", STRONG_SOFTENING: "warn", COOLING: "warn", CONTRACTING: "warn", NARROWING: "warn", DECELERATING: "warn",
    NOT_CLASSIFIABLE: "mute",
};
const CHIP_STYLE = {
    pos: "bg-[hsl(var(--positive-soft))] text-[hsl(var(--positive))]",
    neu: "bg-[hsl(var(--accent-soft))]/70 text-[hsl(var(--accent-ink))]",
    warn: "bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))]",
    mute: "bg-[hsl(var(--surface-sunken))] text-[hsl(var(--mute))]",
};

export const StateChip = ({ value, title }) => {
    if (!value) return <span className="text-[10px] text-[hsl(var(--mute))]">—</span>;
    const family = CHIP_FAMILY[value];
    // NOT_CLASSIFIABLE is a valid published state but visually quieter than analytical states.
    if (family === "mute") {
        return <span title={title} className="text-[9.5px] text-[hsl(var(--mute))]/60 whitespace-nowrap">{enumLabel(value)}</span>;
    }
    const style = CHIP_STYLE[family || "mute"];
    return (
        <span title={title} className={`inline-block px-1.5 py-0.5 rounded text-[9.5px] font-medium leading-none whitespace-nowrap ${style}`}>
            {enumLabel(value)}
        </span>
    );
};

// Market Zone identity chip — same presentationColorToken palette as the
// leaders chart. Links to the existing zone route; no new classification.
export const ZoneChip = ({ zone, zoneKey }) => {
    const label = zone?.label || enumLabel(zoneKey);
    const color = zoneColor(zone?.presentationColorToken || zoneKey);
    return (
        <Link
            to={`/market-v2/market?zone=${encodeURIComponent(zone?.scopeKey || zoneKey)}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`wp12-zone-chip-${zone?.scopeKey || zoneKey}`}
            className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border text-[9.5px] font-medium leading-none whitespace-nowrap hover:underline"
            style={{ borderColor: color.replace(")", " / 0.35)"), background: color.replace(")", " / 0.08)"), color: "hsl(var(--ink-soft))" }}
            title={`${label} — open Market Zone`}
        >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
            {label}
        </Link>
    );
};

const Th = ({ children, tip, tipId, className = "" }) => (
    <th className={`px-2 py-2 ${className}`}>
        <span className="inline-flex items-center gap-1">
            {children}
            {tip && <InfoTip label={`${children} explanation`} testId={tipId}>{tip}</InfoTip>}
        </span>
    </th>
);

export const TopPatternsTable = ({ patternCards, zonesByKey, selectedGeography, geographyLabel, patternGeoFilterAvailable }) => {
    const navigate = useNavigate();
    const rows = useMemo(() => {
        const sorted = [...(patternCards || [])].sort(
            (a, b) => (b.strength?.displayScoreExact ?? -1) - (a.strength?.displayScoreExact ?? -1)
        );
        return sorted.slice(0, 10);
    }, [patternCards]);

    return (
        <div className="card-flat overflow-x-auto" data-testid="wp12-home-top-patterns">
            <div className="px-4 pt-4 pb-2 flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="text-sm font-semibold inline-flex items-center gap-2">
                        {selectedGeography && patternGeoFilterAvailable
                            ? `Top structural patterns observed in ${geographyLabel}`
                            : "Top structural patterns"}
                        {selectedGeography && patternGeoFilterAvailable && (
                            <InfoTip label="Region filtering semantics" testId="wp12-home-table-geo-tip">
                                Region filtering reflects region-linked observed activity. A Pattern's
                                canonical taxonomy label may reference a different geography and does not
                                represent organization domicile or exclusive regional scope.
                            </InfoTip>
                        )}
                    </h3>
                    <p className="text-[10px] text-[hsl(var(--mute))] mt-0.5">
                        {selectedGeography && patternGeoFilterAvailable
                            ? `Patterns observed in ${geographyLabel}-linked activity, ordered by current structural score.`
                            : "Top 10 by current structural score"}
                    </p>
                </div>
                {selectedGeography && !patternGeoFilterAvailable && (
                    <span className="text-[9px] text-[hsl(var(--mute))]" data-testid="wp12-home-pattern-geo-gap">
                        Geographic pattern filtering awaits publisher hydration — showing the global top 10.
                    </span>
                )}
            </div>
            <table className="w-full text-left">
                <thead>
                    <tr className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] border-b border-[hsl(var(--line))]">
                        <th className="px-4 py-2 w-8">#</th>
                        <th className="px-2 py-2">Pattern</th>
                        <th className="px-2 py-2">Market zone</th>
                        <th className="px-2 py-2">Use case</th>
                        <Th className="text-right" tip="Current published structural score for this Pattern." tipId="wp12-tip-score">Score</Th>
                        <Th tip="Published structural direction for the 4-week observation horizon." tipId="wp12-tip-4w">4w</Th>
                        <Th tip="Published structural direction for the 12-week observation horizon." tipId="wp12-tip-12w">12w</Th>
                        <Th tip="Describes the pace of structural movement, not the absolute score level. A Pattern can remain on a rising trajectory while the pace of strengthening is cooling." tipId="wp12-tip-velocity">Velocity</Th>
                        <Th tip="Describes how broadly the observed activity is distributed across the underlying participation footprint. Contracting breadth can coexist with a strong or rising score." tipId="wp12-tip-breadth">Breadth</Th>
                        <Th className="pr-4" tip="Published Pattern score history across the 12-week view. It shows score level over time and is not the same measure as Velocity or Breadth." tipId="wp12-tip-trajectory">12w trajectory</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((c, i) => {
                        const zone = zonesByKey?.[c.marketZoneKey];
                        const h4 = c.horizonSummary?.h4, h12 = c.horizonSummary?.h12;
                        return (
                            <tr
                                key={c.canonicalPatternId}
                                data-testid={`wp12-home-top-pattern-${i + 1}`}
                                onClick={() => navigate(`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`)}
                                className="border-b border-[hsl(var(--line))] last:border-0 hover:bg-[hsl(var(--surface-sunken))] cursor-pointer"
                            >
                                <td className="px-4 py-2.5 num text-[10px] text-[hsl(var(--mute))]">{i + 1}</td>
                                <td className="px-2 py-2.5 max-w-[260px]">
                                    <Link
                                        to={`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`wp12-home-top-pattern-link-${i + 1}`}
                                        className="text-xs font-medium leading-snug truncate block hover:text-[hsl(var(--accent-ink))] hover:underline"
                                        title={!c.displayTitle && selectedGeography && patternGeoFilterAvailable
                                            ? "This is the published canonical Pattern label. The Pattern appears here because it has observed evidence linked to the selected region."
                                            : patternDisplayTitle(c)}
                                    >
                                        {patternDisplayTitle(c)}
                                    </Link>
                                    {c.displayTitle && c.patternLabel && c.displayTitle !== c.patternLabel && (
                                        <div className="text-[9px] text-[hsl(var(--mute))] leading-snug truncate" title={c.patternLabel}>{c.patternLabel}</div>
                                    )}
                                </td>
                                <td className="px-2 py-2.5 whitespace-nowrap"><ZoneChip zone={zone} zoneKey={c.marketZoneKey} /></td>
                                <td className="px-2 py-2.5 text-[10px] text-[hsl(var(--ink-soft))] whitespace-nowrap max-w-[140px] truncate" title={c.useCaseLabel || c.useCaseKey}>{c.useCaseLabel || enumLabel(c.useCaseKey)}</td>
                                <td className="px-2 py-2.5 text-right whitespace-nowrap">
                                    <span className="num text-[11px] font-medium text-[hsl(var(--ink-soft))]">{c.strength?.displayScoreExact != null ? c.strength.displayScoreExact.toFixed(1) : "—"}</span>
                                    <span className="text-[9px] text-[hsl(var(--mute))] ml-1">{enumLabel(c.strength?.scoreBand)}</span>
                                </td>
                                <td className="px-2 py-2.5">
                                    <StateChip value={h4?.trend?.primaryTrendClass} title={`Published: ${enumLabel(h4?.trend?.primaryTrendClass)} · reliability ${enumLabel(h4?.trend?.reliability)} · ${enumLabel(h4?.trend?.claimEligibility)}`} />
                                </td>
                                <td className="px-2 py-2.5">
                                    <StateChip value={h12?.trend?.primaryTrendClass} title={`Published: ${enumLabel(h12?.trend?.primaryTrendClass)} · reliability ${enumLabel(h12?.trend?.reliability)} · ${enumLabel(h12?.trend?.claimEligibility)}`} />
                                </td>
                                <td className="px-2 py-2.5">
                                    <StateChip value={h12?.trend?.velocityState} title={`Published (12w): ${enumLabel(h12?.trend?.velocityState)}`} />
                                </td>
                                <td className="px-2 py-2.5">
                                    <StateChip value={h12?.breadth?.actorTrendClass} title={`Actor breadth (12w): ${enumLabel(h12?.breadth?.actorTrendClass)} · ${enumLabel(h12?.breadth?.actorClaimEligibility)}`} />
                                </td>
                                <td className="px-4 py-2.5">
                                    {(c.scoreTimeline12w || []).length > 1 && (
                                        <Sparkline values={c.scoreTimeline12w.map((p) => p.displaySignalScoreExact)} width={90} height={22} />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
